import { useMemo } from 'react';
import { useLeague } from './useLeague';
import { useUserWallets } from '../useUserData';
import { useUserData } from '../useUserData';

type Mode = 'main' | 'arena';

type LeaguePlayerState = {
  main: {
    total: number;
    used: number;
    available: number;
    selectedTokens: number[];
  };
  arena: {
    total: number;
    used: number;
    available: number;
    selectedTokens: number[];
  };
  selectToken: (tokenId: number, mode?: Mode) => Promise<void>;
};

export const useLeaguePlayerState = (): LeaguePlayerState => {
  const { league, currentSeason } = useLeague();

  // userData SOLO se mantiene por metadata del usuario (clan, perfil, etc.)
  const { addTokenSelection } = useUserData();

  const { wallets } = useUserWallets();

  const leagueId = league?.id;
  const seasonId = currentSeason?.seasonNumber?.toString();

  // -------------------------
  // MAIN TOKENS (SOURCE: wallets.claimed)
  // -------------------------
  const mainSelectedTokens = useMemo(() => {
    if (!leagueId || !seasonId) return [];

    const wallet = wallets.find(
      (w) =>
        w.leagueId === leagueId &&
        String(w.seasonId) === String(seasonId) &&
        w.type === 'main',
    );

    return wallet?.claimed ?? [];
  }, [wallets, leagueId, seasonId]);

  const main = useMemo(() => {
    if (!leagueId || !seasonId) {
      return {
        total: 0,
        used: 0,
        available: 0,
        selectedTokens: [],
      };
    }

    const wallet = wallets.find(
      (w) =>
        w.leagueId === leagueId &&
        String(w.seasonId) === String(seasonId) &&
        w.type === 'main',
    );

    const total = wallet?.available ?? 0;
    const used = mainSelectedTokens.length;
    const available = total;

    return {
      total,
      used,
      available,
      selectedTokens: mainSelectedTokens,
    };
  }, [leagueId, seasonId, wallets, mainSelectedTokens]);

  // -------------------------
  // ARENA TOKENS (SOURCE: wallets.claimed)
  // -------------------------
  const arenaSelectedTokens = useMemo(() => {
    if (!leagueId || !seasonId) return [];

    const wallet = wallets.find(
      (w) =>
        w.leagueId === leagueId &&
        String(w.seasonId) === String(seasonId) &&
        w.type === 'arena',
    );

    return wallet?.claimed ?? [];
  }, [wallets, leagueId, seasonId]);

  const arena = useMemo(() => {
    if (!leagueId || !seasonId) {
      return {
        total: 0,
        used: 0,
        available: 0,
        selectedTokens: [],
      };
    }

    const wallet = wallets.find(
      (w) =>
        w.leagueId === leagueId &&
        String(w.seasonId) === String(seasonId) &&
        w.type === 'arena',
    );

    const total = wallet?.available ?? 0;
    const used = arenaSelectedTokens.length;
    const available = total;

    return {
      total,
      used,
      available,
      selectedTokens: arenaSelectedTokens,
    };
  }, [leagueId, seasonId, wallets, arenaSelectedTokens]);

  // -------------------------
  // ACTION: SELECT TOKEN
  // -------------------------
  const selectToken = async (tokenId: number, mode: Mode = 'main') => {
    if (!leagueId || !seasonId) return;

    await addTokenSelection({
      leagueId,
      seasonId,
      mode,
      tokenId,
    });
  };

  return {
    main,
    arena,
    selectToken,
  };
};

export default useLeaguePlayerState;
